import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

export const useDuplicateTest = () => {
  const [isDuplicating, setIsDuplicating] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const duplicateTest = async (testId: string) => {
    setIsDuplicating(true);
    
    try {
      // 1. Fetch original test
      const { data: originalTest, error: testError } = await supabase
        .from('tests')
        .select('*')
        .eq('id', testId)
        .single();

      if (testError) throw testError;
      if (!originalTest) throw new Error("Test not found");

      // 2. Fetch original questions
      const { data: originalQuestions, error: questionsError } = await supabase
        .from('questions')
        .select('*')
        .eq('test_id', testId)
        .order('order', { ascending: true });

      if (questionsError) throw questionsError;

      // 3. Create new test with modified data
      const newTestData = {
        ...originalTest,
        id: undefined, // Let DB generate new ID
        title: `Copy of ${originalTest.title}`,
        status: 'draft',
        is_active: true,
        share_token: crypto.randomUUID(),
        created_at: undefined, // Let DB set new timestamp
      };

      const { data: newTest, error: createError } = await supabase
        .from('tests')
        .insert(newTestData)
        .select()
        .single();

      if (createError) throw createError;
      if (!newTest) throw new Error("Failed to create test");

      // 4. Copy all questions if they exist
      if (originalQuestions && originalQuestions.length > 0) {
        const newQuestions = originalQuestions.map((q) => ({
          test_id: newTest.id,
          question_type: q.question_type,
          content: q.content,
          time_limit: q.time_limit,
          order: q.order,
          marks: q.marks,
          skill: q.skill,
          difficulty: q.difficulty,
          markscheme: q.markscheme,
          code_tests: q.code_tests,
        }));

        const { error: questionsInsertError } = await supabase
          .from('questions')
          .insert(newQuestions);

        if (questionsInsertError) throw questionsInsertError;
      }

      // 5. Invalidate queries and show success
      queryClient.invalidateQueries({ queryKey: ['user-tests'] });
      
      toast({
        title: "Test duplicated successfully",
        description: `"${newTest.title}" has been created as a draft.`,
      });

      // 6. Navigate to new test
      navigate(`/test/${newTest.id}`);
      
      return newTest.id;
    } catch (error: any) {
      console.error('Error duplicating test:', error);
      toast({
        title: "Failed to duplicate test",
        description: error.message || "An error occurred while duplicating the test.",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsDuplicating(false);
    }
  };

  return {
    duplicateTest,
    isDuplicating,
  };
};
